import React, {Fragment, useState, useEffect, useCallback } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Plus, Save, Trash2, X } from "lucide-react"
import { useAppCtx, appStore } from "lib/state"
import { queryTools, cn, randoms } from "lib/utils" 
import { Menubar } from "lib/components"
import useTemplate from "lib/units/template/useTemplate"
import { StatementContentWrapper, StatementMarker, useStatements } from "."
import { ConditionalEditor, ConditionalProvider, ConditionalWorkingArea, useConditional } from "lib/units/conditional"

const starterStatement = {
  refstr: "", title: "New statement", description: "", visible: true, if_passes: "", if_fails: "",
  conditional: { source_id: null, name: "Another Test 2", description: "", zones: [], items: [], clipboard: [] },
  content: { pass: "", fail: "" }
}

export default function Statement({ 
  template, 
  statementObj, 
  saveTemplateFn,
  updateTemplateWithStatement 
}:{ 
  template:any; 
  statementObj:any
  saveTemplateFn:any;
  updateTemplateWithStatement:any
}){

  const { tmplid } = useParams()
  const [ searchParams, setSearchParams ] = useSearchParams()
  const { appState } = useAppCtx()
  const { store, AppStore }:any = appStore()

  const { update, synchronizeStatements } = AppStore

  const { templateTools } = useTemplate(template)
  const { statementTools } = useStatements()
  const { saveConditionalObj }:any = useConditional()

  // const { getCurrentStatement, switchActiveTab, statements, setResource, synchronizeStatements }:any = useAppStore()
  // const { getConfig, createStatement, getStatementParents, saveStatement, updateStatement, updateStatementMeta } = useStatement()

  const { data:_Template } = queryTools.resource.parseQueryForApi("template.findById", { _id: tmplid })
  const saveFn = queryTools.resource.save(["template.all"])

  async function handleTemplateSave(data:any){
    saveFn.mutate({ resource: "template", data: {...data, _id: _Template._id} })
  }

  // const [ editingArea, setEditingArea ] = useState<string>("conditions") 
  const [ childStatements, setChildStatements ] = useState<any>({ pass: null, fail: null, visible: null })
  const [ activeTab, setActiveTab ] = useState<string>()
  const [ viewNestedStatement, setViewNestedStatement ] = useState<any>()
  const [ showDetailForm, setShowDetailForm ] = useState<boolean>(false)
  const [ detailForm, setDetailForm ] = useState<any>({ title: store.actives.statement.title, description: store.actives.statement.description })

  const saveStatementToTemplate = (statementObj:any) => {
    templateTools.statement.update(statementObj, (data:any) => handleTemplateSave(data))
  }

  const handleStatementSave = (e:any) => {
    e.preventDefault()
    const updatedStatement = {...store.actives.statement, ...detailForm }
    update("actives.statement", updatedStatement)
    saveStatementToTemplate(updatedStatement)
  }

  /* ===== when user chooses the "if passes" or "if fails" option for a statement ======= */
  const handleResultChange = (condition:string, action:string) => {
    if( store.actives.statement[condition] !== action ){
      const newStatement = { ...store.actives.statement, [condition]: action }
      update("actives.statement", newStatement)
    }
  }

  const showDetailsMenu = () => {
    return showDetailForm === true ? false : true
  }

  const isSelectedResult = (condition:string, action:string) => store.actives.statement[condition] === action

  const passOpts = [
    { value: "content", label: "Show Content" },
    { value: "statement", label: "Proceed To Nested Statement" },
    { value: "content+statement", label: "Show Content and Proceed" },
    { value: "nothing", label: "Do Nothing" }
  ]

  const failOpts = [
    { value: "content", label: "Show Content" },
    { value: "nothing", label: "Do Nothing" }
  ]

  const menus = [
    { 
      refstr: "details", 
      label: "Details",
      visibleFn: () => {
        return (showDetailForm === true) ? false : true
      },
      submenus: [
        { type: "option", refstr: "update_details", label: "Update Details", selectFn: () => setShowDetailForm(true) },
      ]
    },
    { 
      refstr: "view", 
      label: "View",
      submenus: [
        { type: "option", refstr: "conditionals", label: "Conditionals", selectFn: () => setActiveTab("conditions") },
        { type: "option", refstr: "content", label: "Content", selectFn: () => setActiveTab("content") },
      ]
    },
    { 
      refstr: "logic", 
      label: "Logic",
      submenus: [
        { type: "option", refstr: "if_passes", label: "If Statement Passes" },
        { type: "option", refstr: "if_passes_content", label: "Content Only", isSelected: isSelectedResult("if_passes", "content"), selectFn: () => handleResultChange("if_passes", "content") },
        { type: "option", refstr: "if_passes_content+statement", label: "Content + Statement", isSelected: isSelectedResult("if_passes", "content+statement"), selectFn: () => handleResultChange("if_passes", "content+statement") },
        { type: "option", refstr: "if_passes_statement", label: "Statement Only", isSelected: isSelectedResult("if_passes", "statement"), selectFn: () => handleResultChange("if_passes", "statement") },
        { type: "option", refstr: "if_passes_nothing", label: "Nothing", isSelected: isSelectedResult("if_passes", "nothing"), selectFn: () => handleResultChange("if_passes", "nothing") },
        { type: "divider", refstr: "divider1", label: "---" },
        { type: "option", refstr: "if_fails", label: "If Statement Fails" },
        { type: "option", refstr: "if_fails_content", label: "Content Only", isSelected: isSelectedResult("if_fails", "content"), selectFn: () => handleResultChange("if_fails", "content") },
        { type: "option", refstr: "if_fails_content+statement", label: "Content + Statement", isSelected: isSelectedResult("if_fails", "content+statement"), selectFn: () => handleResultChange("if_fails", "content+statement") },
        { type: "option", refstr: "if_fails_statement", label: "Statement Only", isSelected: isSelectedResult("if_fails", "statement"), selectFn: () => handleResultChange("if_fails", "statement") },
        { type: "option", refstr: "if_fails_nothing", label: "Nothing", isSelected: isSelectedResult("if_fails", "nothing"), selectFn: () => handleResultChange("if_fails", "nothing") },

        { 
          type: "divider", 
          refstr: "divider1", 
          label: "---",
          visibleFn: () => showMenuOptsForAnyNestedStatement()
        },

        { 
          type: "option", 
          refstr: "add_nested", 
          label: "Add Nested Statement",
          visibleFn: () => showMenuOptsForAnyNestedStatement()
        },

        { 
          type: "option", 
          refstr: "add_nested_pass", 
          label: "Add Nested Statement for Pass", 
          isSelected: false, 
          selectFn: () => {}, // StatementTools.createNestedStatement("if_passes"),
          visibleFn: () => showMenuOptsForResultType("if_passes")
        },

        { 
          type: "option", 
          refstr: "add_nested_fail", 
          label: "Add Nested Statement for Fail", 
          isSelected: false, 
          selectFn: () => {}, // StatementTools.createNestedStatement("if_fails"),
          visibleFn: () => store.actives.statement.if_fails !== "nothing"
        },
      ]
    },
    { 
      refstr: "save", 
      label: "Save",
      submenus: [
        { 
          type: "option", 
          refstr: "save-template", 
          label: "Save Changes ", 
          selectFn: () => saveStatementToTemplate(store.actives.statement)
        },
      ]
    },
    { 
      refstr: "close", 
      label: "Close",
      edge: "right",
      submenus: [
        { type: "option", refstr: "save", label: "Return to Main View", selectFn: () => update("actives.statement", null) }
      ]
    },
  ]

  const showMenuOptsForResultType = (type:string) => {
    let verdict = false
    // console.log(store.actives.statement)
    // we have chosen to use a nested statement, but none has been created
    if( !store.actives.statement.nested?.[type] && store.actives.statement[type].includes("statement") ){
      verdict = true
    }
    return verdict
  }

  const showMenuOptsForAnyNestedStatement = () => {
    return showMenuOptsForResultType("if_passes") || showMenuOptsForResultType("if_fails")
  }

  const getStatementParents = () => {
    const currentLevel = store.actives.statement.level
    if( currentLevel === 1 ) return []
    return statementTools.search(store.actives.statement.parent)
  }


  /* ====== when user clicks on conditionals/content buttons ====== */
  const handleButtonClick = (area:string) => {
    console.log(activeTab, area)
    setActiveTab(area)
    // setStatements( (prev:any) => prev.map( (st:any) => (st.refstr === selectedStatement.refstr ) ? { ...st, _meta: { ...st._meta, activeTab: area }} : st ))
  }


  function createStatement(parent:string, level:number, parentResult:string){
    const newStatement = { ...starterStatement, refstr: randoms.string(12, "a#"), parent, level, parentResult }
    update("config.statements", [...store.config.statements, newStatement])
    update("collections.statement", [...store.collections.statement, newStatement])
  }

  /* ====== shared props for the conditions/content buttons ====== */
  const getButtonProps = (area:string) => ({
    className: cn("h-[25px] px-5 text-xs uppercase tracking-wide bg-slate-700/50", activeTab === area && "bg-green-800 text-gray-200"),
    onClick: (e:any) => {
      e.preventDefault()
      handleButtonClick(area)
    }
  })

  const goToParent = (parentObj:any) => {
    console.log("send user to:", parentObj.refstr)
    update("actives.statement", parentObj)
    // update("statementLoadParams", { parent: parentObj.parent[0], level: parentObj.level, parentResult: parentObj.parentResult })
  }


  /* ====== to load a pass/fail statment, set the visible prop to pass or fail of childStatements ====== */
  const loadNestedStatement = (type:string) => {
    const newStatementParent = store.actives.statement.refstr
    const newLevel = store.actives.statement.level + 1
    update("statementLoadParams", { parent: newStatementParent, level: newLevel, parentResult: type })
  }

  const handleFieldChange = (e:React.ChangeEvent<HTMLInputElement>) => setDetailForm({ ...detailForm, [e.target.name]: e.target.value })
  const handleSaveClick = () => {
    if( showDetailForm === false ){
      setShowDetailForm(true)
    } else {
      // handleStatementSave(null, { ...store.actives.statement, ...detailForm })
      setShowDetailForm(false)
    }
  }
  
  const fieldClasses = "w-full h-full border border-solid border-slate-700 bg-slate-900 px-2 focus:outline-0 text-white"
  const btnClasses= "w-16 h-full flex justify-center items-center border border-solid border-teal-900 cursor-pointer px-2 bg-teal-800 uppercase text-xs"

  useEffect(() => {
    if( !activeTab ){
      const tab = searchParams.get("elem") === "compelling_evidence" ? "content" : "conditions"
      setActiveTab(tab)
    }
  },[activeTab, store.element.refstr])

  if( !store.actives.statement || !activeTab ) return <></>
  return (
    <div 
      className={
        cn(
          "w-full h-full border border-solid border-slate-700 relative p-[2px]", 
          store.actives.statement?.refstr === statementObj?.refstr ? "*:text-white" : "*:text-slate-600" 
        )
      }
    >
      <div className="std-block-title justify-between px-2">
        <div className={cn("h-full text-sm flex justify-start items-center px-3 uppercase")}>
          { showDetailForm === false && <span>{ statementObj.title }</span> }
          { showDetailForm === true && (
            <form className="w-[95%] h-full flex justify-start items-center gap-2">
              <input type="text" name="title" placeholder="Statement Title" value={detailForm.title || ""} onChange={handleFieldChange} className={cn(fieldClasses, "w-3/12")} />
              <input type="text" name="description" placeholder="Description" value={detailForm.description || ""} onChange={handleFieldChange} className={cn(fieldClasses, "w-9/12")} />
              <button className={cn(btnClasses)} onClick={() => setShowDetailForm(false)}>cancel</button>
              <button className={cn(btnClasses)} onClick={handleStatementSave}>Save</button>
            </form>
          )}
        </div>
        <div className="">
          {/* <div className="w-full flex justify-end items-center"> */}
            <Menubar menus={menus} />
          {/* </div> */}
        </div> 
      </div>

      { getStatementParents !== undefined && store.actives.statement.level > 1 && (
        <div className="w-8/12 mx-auto flex justify-center items-center gap-2 my-3 border border-solid border-slate-700 py-1 text-xs">
          <div className="text-xs uppercase">
            Return to 
            <span 
              className="text-yellow-500 cursor-pointer pl-1" 
              onClick={() => update("actives.statement", getStatementParents())}
            >
              parent statement
            </span>
          </div>
        </div>
      )}

      { store.actives.statement.refstr === statementObj.refstr && activeTab?.length === 0 && (
        <div className="w-full flex px-5 py-2 gap-2">
          <span className="text-green-400">PASS: </span>
          <div className="w-full" dangerouslySetInnerHTML={{ __html: store.actives.statement.content.pass }}></div>
        </div>
      )}

      { store.actives.statement.refstr === statementObj.refstr && (
        <div className="w-full my-3">
          { activeTab === "content" ? (
            <StatementContentWrapper />
          ):(
            <div className="w-full flex flex-col gap-2 relative">
              <ConditionalEditor conditionalObj={store.actives.statement.conditional} />

              { (store.actives.statement.nested.if_passes !== null || store.actives.statement.nested.if_fails !== null) && (
                <div className="w-[97%] mx-auto my-4 flex flex-col gap-2">
                  <div className="w-[97%] mx-auto my-4 flex gap-2">
                    { ["if_passes", "if_fails"].map( (type:string) => (
                      <Fragment key={type}>
                        <div 
                          onClick={() => setViewNestedStatement(store.actives.statement.nested[type])}
                          className={cn(
                            "w-full py-1 flex justify-center items-center text-sm uppercase border border-solid border-slate-700 cursor-pointer",
                            store.actives.statement.nested[type] === null && "hidden",
                            viewNestedStatement !== undefined && viewNestedStatement?.parentResult === type && type === "if_passes" && "bg-green-700/30",
                            viewNestedStatement !== undefined && viewNestedStatement?.parentResult === type && type === "if_fails" && "bg-red-700/30"
                          )}
                        >
                          { type.replace("_", " ") }
                        </div>
                      </Fragment>
                    ))}
                  </div>

                  { store.actives.statement !== null && viewNestedStatement !== undefined && (
                    <div className="w-[98%] mx-auto border border-solid border-slate-700 p-[4px]">
                      <StatementMarker 
                        template={_Template}
                        statementObj={viewNestedStatement} 
                        saveTemplateFn={saveTemplateFn}
                        updateTemplateWithStatement={updateTemplateWithStatement} 
                      /> 
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      )}
    </div>
  )
}
